import {useCallback, useMemo, useState} from 'react';
import {AIMessageGeneratorProps} from './utils/types';
import {useAIMessageGenerator} from './utils/useAIMessageGenerator';
import {useGeneratorPrompt} from './utils/useGeneratorPrompt';
import AIMessageGeneratorPanel from './AIMessageGeneratorPanel';
import AIMessageGeneratorHeader from './AIMessageGeneratorHeader';
import AIMessageGeneratorContainer from './AIMessageGeneratorContainer';
import useMobile from '../../hooks/useMobile';

function AIMessageGenerator({
    show,
    title,
    placeholder,
    newChatLabel,
    submit,
    approve,
    errorMessage,
    suggestions,
    analytics,
    container = 'sidePopup',
    renderEmptyState,
    renderPromptArea,
    renderUserMessage,
    renderAssistantFooter,
    onReset,
    closeOnOutsideClick = true,
    hideBackdrop = false,
    resetOnClose = true,
    dataCy = 'ai-message-generator',
}: AIMessageGeneratorProps) {
    const isMobile = useMobile();
    const [hasPendingAttachment, setHasPendingAttachment] = useState(false);

    // Every reset remounts the prompt area, so the pending attachment it held is gone too.
    const handleReset = useCallback(() => {
        setHasPendingAttachment(false);
        onReset?.();
    }, [onReset]);

    const generator = useAIMessageGenerator({
        errorMessage, submit, approve, onClose: show.close, onReset: handleReset, analytics,
    });
    const {messages, reset} = generator;

    const prompt = useGeneratorPrompt({
        promptText: generator.promptText,
        setPromptText: generator.setPromptText,
        draftLoading: generator.draftLoading,
        hasPendingAttachment,
        submitPrompt: generator.submitPrompt,
        analytics,
    });

    const closePopup = useCallback(async () => {
        await show.close();
        if(resetOnClose) {
            reset();
        }
    }, [show, reset, resetOnClose]);

    const {logAnalytics} = prompt;
    const handleNewChat = useCallback(() => {
        logAnalytics('ai_message_generator_new_chat_click', {button_name: 'new_chat'});
        reset();
    }, [logAnalytics, reset]);

    // SidePopup wires backdrop + outside-click to show.close. Keep header close via closePopup.
    const sidePopupShow = useMemo(
        () => (closeOnOutsideClick ? show : {...show, close: async () => undefined}),
        [show, closeOnOutsideClick],
    );
    const innerPopShow = useMemo(() => ({...show, close: closePopup}), [show, closePopup]);

    return (
        <AIMessageGeneratorContainer
            container={container}
            show={sidePopupShow}
            innerPopShow={innerPopShow}
            title={title}
            closeOnOutsideClick={closeOnOutsideClick}
            hideBackdrop={hideBackdrop}
            dataCy={dataCy}
        >
            <AIMessageGeneratorHeader
                title={title}
                onClose={closePopup}
                hasMessages={messages.length > 0}
                onNewChat={handleNewChat}
                newChatLabel={newChatLabel}
            />
            <AIMessageGeneratorPanel
                generator={generator}
                prompt={prompt}
                suggestions={suggestions}
                placeholder={placeholder}
                submit={submit}
                approve={approve}
                isMobile={isMobile}
                hasPendingAttachment={hasPendingAttachment}
                onPendingAttachmentChange={setHasPendingAttachment}
                renderEmptyState={renderEmptyState}
                renderPromptArea={renderPromptArea}
                renderUserMessage={renderUserMessage}
                renderAssistantFooter={renderAssistantFooter}
                innerPop={container === 'innerPop'}
            />
        </AIMessageGeneratorContainer>
    );
}

export default AIMessageGenerator;
